import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  Buildings,
  Copy,
  ArrowsClockwise,
  PlusCircle,
  CheckCircle,
  XCircle,
  Users,
} from "@phosphor-icons/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { formatDateTime } from "@/lib/format";

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${text}`);
  } catch {
    toast.error("Copy failed");
  }
}

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/companies"),
        api.get("/company-applications"),
      ]);
      setCompanies(c.data);
      setApplications(a.data);
    } catch (e) {
      console.error("[AdminCompanies]", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const regen = async (id) => {
    if (!window.confirm("Rotate the joining code? Existing users are unaffected but new joiners must use the new code.")) return;
    try {
      const res = await api.post(`/companies/${id}/regenerate-code`);
      toast.success(`New code: ${res.data.joining_code}`);
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const approve = async (aid) => {
    try {
      const res = await api.post(`/company-applications/${aid}/approve`);
      toast.success(`Approved — company code ${res.data.company.joining_code}`);
      fetchAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to approve");
    }
  };

  const reject = async (aid) => {
    if (!window.confirm("Reject this application?")) return;
    try {
      await api.post(`/company-applications/${aid}/reject`);
      toast.success("Application rejected");
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const pending = applications.filter((a) => a.status === "PENDING");

  return (
    <Layout>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console · Companies</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Companies & joining codes
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-2xl">
          Generate a joining code for each client company. Share the code — they'll enter it during onboarding to link their account to your workspace.
        </p>
      </div>

      {/* Applications */}
      {pending.length > 0 && (
        <div className="mb-8 bg-white border-2 border-[#FF5A1F] p-6" data-testid="pending-applications-panel">
          <div className="mt-tiny text-[#FF5A1F] mb-3">Pending applications · {pending.length}</div>
          <div className="grid gap-3">
            {pending.map((a) => (
              <div key={a.application_id} className="border border-zinc-200 p-4 flex flex-wrap items-start gap-4" data-testid={`application-row-${a.application_id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="font-display font-bold text-lg text-zinc-900">{a.proposed_company_name}</div>
                  <div className="mt-tiny text-zinc-500 mt-1">
                    {a.requested_by_name} · {a.requested_by_email} · {a.profession} · {a.contact_number}
                    {a.contact_email && a.contact_email !== a.requested_by_email
                      ? ` · ${a.contact_email}`
                      : ""}
                  </div>
                  {a.notes && <div className="text-sm text-zinc-600 mt-2">{a.notes}</div>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => approve(a.application_id)}
                    data-testid={`approve-application-${a.application_id}`}
                    className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-medium"
                  >
                    <CheckCircle size={14} weight="fill" /> Approve & create
                  </button>
                  <button
                    onClick={() => reject(a.application_id)}
                    data-testid={`reject-application-${a.application_id}`}
                    className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-red-500 hover:text-red-500 px-4 py-2 text-sm font-medium"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end mb-6">
        <button
          onClick={() => setCreating(true)}
          data-testid="new-company-button"
          className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-5 py-3 font-medium transition-colors"
        >
          <PlusCircle size={18} weight="fill" /> New Company
        </button>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="companies-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Company</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Joining code</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Members</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Contact</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Created</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              ["s-1", "s-2", "s-3"].map((k) => (
                <TableRow key={k}><TableCell colSpan={6}><div className="h-14 bg-zinc-100 animate-pulse" /></TableCell></TableRow>
              ))
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-14 text-zinc-500">
                  <Buildings size={40} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
                  No companies yet — click "New Company" to generate the first joining code.
                </TableCell>
              </TableRow>
            ) : (
              companies.map((c) => (
                <TableRow key={c.company_id} className="hover:bg-zinc-50 border-zinc-200" data-testid={`company-row-${c.company_id}`}>
                  <TableCell>
                    <div className="font-medium text-zinc-900">{c.name}</div>
                    {c.address && <div className="mt-tiny text-zinc-400">{c.address}</div>}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => copyToClipboard(c.joining_code)}
                      data-testid={`copy-code-${c.company_id}`}
                      className="inline-flex items-center gap-2 font-mono-mt text-sm font-semibold text-[#0047AB] hover:underline"
                    >
                      {c.joining_code} <Copy size={14} />
                    </button>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-sm text-zinc-700">
                      <Users size={14} /> {c.member_count ?? 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600">
                    <div>{c.contact_email || "—"}</div>
                    <div className="mt-tiny text-zinc-400">{c.contact_phone || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">{formatDateTime(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => regen(c.company_id)}
                      data-testid={`regen-code-${c.company_id}`}
                      className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-amber-500 hover:text-amber-600 px-3 py-1.5 text-xs font-medium"
                    >
                      <ArrowsClockwise size={14} /> Rotate code
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <NewCompanyDialog open={creating} onOpenChange={setCreating} onCreated={fetchAll} />
    </Layout>
  );
}

function NewCompanyDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);

  useEffect(() => {
    if (open) {
      setName(""); setAddress(""); setEmail(""); setPhone(""); setCreatedCode(null);
    }
  }, [open]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Company name is required");
    setBusy(true);
    try {
      const res = await api.post("/companies", {
        name: name.trim(),
        address: address.trim(),
        contact_email: email.trim() || null,
        contact_phone: phone.trim(),
      });
      setCreatedCode(res.data.joining_code);
      onCreated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-zinc-200 max-w-lg" data-testid="new-company-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {createdCode ? "Company created" : "New company"}
          </DialogTitle>
          <DialogDescription>
            {createdCode
              ? "Share the joining code with your client's team. They'll enter it during onboarding to link their account."
              : "We'll generate a unique joining code you can share with the client. They can then link their accounts to this workspace."}
          </DialogDescription>
        </DialogHeader>

        {createdCode ? (
          <div className="space-y-4">
            <div className="bg-[#0047AB]/5 border border-[#0047AB]/30 p-6 text-center">
              <div className="mt-tiny text-[#0047AB] mb-2">Joining code</div>
              <div className="font-mono-mt text-3xl font-black text-[#0047AB] tracking-widest" data-testid="created-joining-code">{createdCode}</div>
              <button
                onClick={() => copyToClipboard(createdCode)}
                data-testid="copy-created-code-button"
                className="mt-4 inline-flex items-center gap-2 border border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB] hover:text-white px-4 py-2 text-sm font-medium transition-colors"
              >
                <Copy size={14} /> Copy to clipboard
              </button>
            </div>
            <div className="text-xs text-zinc-500 text-center">
              Store this somewhere safe — you can always rotate it from the table.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Company name *</div>
              <Input
                data-testid="new-company-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="BuildCorp Interiors LLC"
                className="h-11 rounded-none border-zinc-200"
              />
            </div>
            <div>
              <div className="mt-tiny text-zinc-500 mb-1.5">Address</div>
              <Input
                data-testid="new-company-address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Optional"
                className="h-11 rounded-none border-zinc-200"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Contact email</div>
                <Input
                  data-testid="new-company-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ops@…"
                  className="h-11 rounded-none border-zinc-200"
                />
              </div>
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Contact phone</div>
                <Input
                  data-testid="new-company-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 000 …"
                  className="h-11 rounded-none border-zinc-200"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {createdCode ? (
            <button
              onClick={() => onOpenChange(false)}
              className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-2.5 font-medium"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={() => onOpenChange(false)}
                className="border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                data-testid="new-company-submit-button"
                className="bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60"
              >
                {busy ? "Creating..." : "Generate company & code"}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
